/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import {
  Box,
  IconButton,
  Pagination,
  Select,
  Table,
  TableBody,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import DownloadIcon from '@mui/icons-material/Download';
import authenticatedFetch from "../../../api/authenticatedFetch";
import camelize from "../../../utils/camelize.js";
import { Loader } from "../../../components/loader";
import {
  StyledPaginationContainer,
  StyledPerPageContainer,
  StyledTableBodyCell,
  StyledTableContainer,
  StyledTableHead,
  StyledTableHeadCell,
  StyledTableRow,
  StyledTypography,
} from "../../../components/index_table/styles";
import { buildQueryParams } from "../../../utils/buildQueryParams.js";
import { useNotificationContext } from "../../../contexts/notificationContext.jsx";
import csvLoader from '../../../assets/images/csv2.gif'
import { scrollbarStyles } from "../styles";
import { StyledMenuItem } from "../../../components/styled_menu_item/index.jsx";
import { perPageStyles, StyledTypography as TableDetailText } from '../../reports/homepage/styles.js'
import { useFillViewportHeight } from "../../../hooks/useFillViewportHeight";

export const ALL_VALUE = 'All'
const ALL_NAMES = [ALL_VALUE]

const normalizeKey = (label) => label?.split(' ').join('_');

// Perq matrix table shared by the Restaurant and Group tabs: one row per "from" entity, one
// column per "to" entity. `selectedNames` narrows rows/columns by name; [ALL_VALUE] shows all.
// Shows the loader itself so sort survives a refetch; a new `resetKey` returns to page 1.
export const ReportMatrix = ({ data, isFetching, resetKey, report, params, storageKey, selectedNames = ALL_NAMES }) => {
  const [entriesPerPage, setEntriesPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [csvLoading, setCsvLoading] = useState(false)
  const [scrollWidth, setScrollWidth] = useState(0);
  const [appliedResetKey, setAppliedResetKey] = useState(resetKey);

  if (appliedResetKey !== resetKey) {
    setAppliedResetKey(resetKey);
    setEntriesPerPage(25);
    setCurrentPage(1);
  }
  const showNotification = useNotificationContext()
  const tableRef = useRef(null);
  const fakeScrollRef = useRef(null);
  const tableFooterRef = useRef(null);
  const resizingRef = useRef({ columnKey: null, startX: 0, startWidth: 0 });
  const { t } = useTranslation();

  const containerMaxHeight = useFillViewportHeight({
    containerRef: tableRef,
    bottomRef: tableFooterRef,
  });

  const handleOrderCsvExport = async () => {
    try {
      setCsvLoading(true);
      const formattedCsvFilters = {
        ...params,
        ...(orderBy ? { sort_by: orderBy, sort_order: order } : {}),
      }
      const query = buildQueryParams(formattedCsvFilters)
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=${report}&${query}`;

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      let date = new Date
      let filename = `${report}_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification('CSV downloaded successfully', 'success');
    } catch (error) {
      console.error('CSV download error:', error);
      showNotification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setCsvLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    if (!data) return []

    if (selectedNames.includes(ALL_VALUE)) return data;

    if (selectedNames.length === 0) return []

    return data.filter(item =>
      selectedNames.includes(item["from/To"])
    );
  }, [data, selectedNames])

  const tableColumns = useMemo(() => {
    if (!data) return [];

    if (!selectedNames.includes(ALL_VALUE) && selectedNames.length === 0) return [];

    // The label first: JavaScript lists integer-like keys (a group named "1856") before every other key.
    const originalColumnOrder = data.length > 0
      ? ["from/To", ...Object.keys(data[0]).filter((col) => col !== "from/To")]
      : [];
    const filteredColumns = originalColumnOrder.filter(col =>
      col === "from/To" || selectedNames.includes(ALL_VALUE) || selectedNames.includes(col)
    );
    return filteredColumns;
  }, [data, selectedNames]);

  const columns = useMemo(() => tableColumns.map((column) => ({
    key: column,
    label: camelize(column),
  })), [tableColumns]);

  // Object.fromEntries defines each key, so a column named "__proto__" stays a cell.
  const rows = useMemo(() => filteredData.map((record) => Object.fromEntries(tableColumns.map(
    (item) => [
      item,
      record[item] !== null && record[item] !== undefined
        ? item === "from/To"
          ? record[item]
          : `$${Number(record[item]).toFixed(2) ?? '0.00'}`
        : "-",
    ],
  ))), [filteredData, tableColumns]);

  const columnTotals = useMemo(() => columns.slice(1).map((col) => (
    filteredData.reduce((sum, item) => {
      const value = item?.[col.key];
      return sum + (typeof value === "number" ? value : 0);
    }, 0)
  )), [columns, filteredData]);

  const totalItems = rows?.length;
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const totalPages = Math.ceil(totalItems / entriesPerPage);

  const isDiscountCardsPath =
    /^\/admin\/perq_cards\/?$/.test(location.pathname) ||
    /^\/admin\/perq_cards\/create\/?$/.test(location.pathname) ||
    /^\/admin\/perq_cards\/edit\/\d+\/?$/.test(location.pathname);

  const handleSort = (property) => {
    const isAsc = orderBy === property.key && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property.key);
  };

  function descendingComparator(a, b, orderBy) {
    const isNumeric = (string) => /^[+-/$]?\d+(\.\d+)?$/.test(string);

    if (isNumeric(a[orderBy]) && isNumeric(b[orderBy])) {
      if (a[orderBy][0] === "$" && b[orderBy][0] === "$") {
        return (
          Number(b[orderBy].substring(1)) - Number(a[orderBy].substring(1))
        );
      }
      return b[orderBy] - a[orderBy];
    }

    if ((b[orderBy] || '') < (a[orderBy] || '')) {
      return -1;
    }
    if ((b[orderBy] || '') > (a[orderBy] || '')) {
      return 1;
    }
    return 0;
  }

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  const visibleRows = useMemo(() => {
    if(rows){
      return [...rows].sort(getComparator(order, orderBy));
    }
  }, [rows, order, orderBy]);

  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) return JSON.parse(saved);
    return columns.reduce((acc, col) => {
      acc[normalizeKey(col.key)] = 150;
      return acc;
    }, {});
  });

  // Handles horizontal scroll
  const handleMainScroll = () => {
    if (!tableRef.current || !fakeScrollRef.current) return;
    fakeScrollRef.current.scrollLeft = tableRef.current.scrollLeft;
  };

  const handleFakeScroll = () => {
    if (!tableRef.current || !fakeScrollRef.current) return;
    tableRef.current.scrollLeft = fakeScrollRef.current.scrollLeft;
  };

  useEffect(() => {
    if (tableRef.current) {
      setScrollWidth(tableRef.current.scrollWidth);
    }
  }, [columnWidths, rows]);

  useEffect(() => {
    const onResize = () => {
      if (tableRef.current) {
        setScrollWidth(tableRef.current.scrollWidth);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Handles column resizing
  const startResizing = (key, e) => {
    e.preventDefault();
    resizingRef.current = { columnKey: normalizeKey(key), startX: e.clientX, startWidth: columnWidths[normalizeKey(key)] };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
  };

  const handleMouseMove = useCallback((e) => {
    const { columnKey, startX, startWidth } = resizingRef.current;
    if (!columnKey) return;
    const newWidth = Math.max(80, startWidth + e.clientX - startX);
    setColumnWidths((prev) => ({ ...prev, [columnKey]: newWidth }));
  }, []);

  const handleMouseUp = useCallback((e) => {
    const { columnKey, startX, startWidth } = resizingRef.current;
    if (!columnKey) return;

    const finalWidth = Math.max(80, startWidth + e.clientX - startX);

    localStorage.setItem(storageKey, JSON.stringify({ ...columnWidths, [columnKey]: finalWidth }));
    setColumnWidths((prev) => ({ ...prev, [columnKey]: finalWidth }));

    resizingRef.current = { columnKey: null, startX: 0, startWidth: 0 };
    document.body.style.cursor = "";

    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  }, [columnWidths, storageKey, handleMouseMove]);

  if (isFetching) return <Loader />;

  return (
    <>
      <StyledPerPageContainer>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Select
            value={entriesPerPage}
            size="small"
            sx={perPageStyles}
            onChange={(e) => setEntriesPerPage(e.target.value)}
          >
            {[10, 25, 50, 100].map((num) => (
              <StyledMenuItem
                key={num}
                value={num}
                sx={{ fontSize: "0.875rem" }}
              >
                {num}
              </StyledMenuItem>
            ))}
          </Select>
          <TableDetailText
            variant="body2"
          >
            entries per page
          </TableDetailText>
        </div>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <TableDetailText
            variant="body2"
          >
            Showing {totalItems === 0 ? 0 : startIndex + 1} to{" "}
            {Math.min(endIndex, totalItems)} of {totalItems} report
            entries
          </TableDetailText>
          {
            csvLoading ? (
              <IconButton disableRipple data-testid="restaurant-discount-export-loading">
                <img style={{ height: 24 }} src={csvLoader} alt="loading..." />
              </IconButton>
            ) : (
              <IconButton
                data-testid="restaurant-discount-export-btn"
                onClick={handleOrderCsvExport}
                disabled={filteredData?.length === 0 || csvLoading}
                sx={{color: '#2b4fb9'}}
              >
                <DownloadIcon />
              </IconButton>
            )
          }
        </Box>
      </StyledPerPageContainer>

      <Box sx={{ position: "relative" }}>
        <StyledTableContainer
          ref={tableRef}
          onScroll={handleMainScroll}
          sx={{
            mt: 1,
            boxShadow: "none",
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: containerMaxHeight,
          }}
        >
          <Table>
            <StyledTableHead report={false}>
              <TableRow>
                {columns.map((col, index) => (
                  <StyledTableHeadCell key={index} sx={{ width: columnWidths[normalizeKey(col?.key)], minWidth: 80, position: "relative", userSelect: "none" }}>
                    <TableSortLabel
                      active={orderBy === col.key}
                      direction={orderBy === col.key ? order : "asc"}
                      onClick={() => handleSort(col)}
                    >
                      {col.label}
                    </TableSortLabel>
                    <Box
                      onMouseDown={(e) => startResizing(col.key, e)}
                      sx={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        height: "100%",
                        width: "6px",
                        cursor: "col-resize",
                        zIndex: 10,
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.1)" },
                      }}
                    />
                  </StyledTableHeadCell>
                ))}
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {visibleRows?.length === 0 ? (
                <StyledTableBodyCell colSpan={columns.length}>
                  <StyledTypography variant="body2">
                    {isDiscountCardsPath
                      ? t("discount-cards.index.table.no-data-exists")
                      : "No Data Exists"}
                  </StyledTypography>
                </StyledTableBodyCell>
              ) : (
                <>
                  <StyledTableRow report={false}>
                    <StyledTableBodyCell
                      report={false}
                      sx={{ color: "rgba(34, 71, 172, 1)" }}
                    >
                      Total Perqs
                    </StyledTableBodyCell>
                    {columnTotals.map((columnTotal, index) => (
                      <StyledTableBodyCell
                        report={false}
                        key={index}
                        sx={{ color: "rgba(34, 71, 172, 1)" }}
                      >
                        {`$${columnTotal.toFixed(2)}`}
                      </StyledTableBodyCell>
                    ))}
                  </StyledTableRow>
                  {visibleRows?.slice(startIndex, endIndex).map((item, index) => (
                    <StyledTableRow report={false} key={index}>
                      {columns.map((col, index) => (
                        <StyledTableBodyCell report={false} key={index} sx={{width: columnWidths[col?.key], minWidth: 80}}>
                          {item[col.key]}
                        </StyledTableBodyCell>
                      ))}
                    </StyledTableRow>
                  ))}
                </>
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>

        <Box ref={tableFooterRef} sx={{ display: 'flow-root' }}>
          <Box
            ref={fakeScrollRef}
            onScroll={handleFakeScroll}
            sx={scrollbarStyles}
          >
            <Box
              sx={{
                width: scrollWidth || '100%',
                height: "1px",
              }}
            />
          </Box>

          <StyledPaginationContainer>
            <Pagination
              page={currentPage}
              count={totalPages}
              onChange={(_event, page) => setCurrentPage(page)}
            />
          </StyledPaginationContainer>
        </Box>
      </Box>
    </>
  );
};
